import { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Button,
  Stack,
  Typography,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  Tooltip,
  Card,
  Grid,
  Divider,
  Tabs,
  Tab,
  useTheme,
  alpha,
  Paper,
} from '@mui/material';
import { GridActionsCellItem, GridColDef } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { formatRelative } from 'date-fns';

import AccountTreeIcon from '@mui/icons-material/AccountTree';
import LaunchIcon from '@mui/icons-material/Launch';
import VisibilityIcon from '@mui/icons-material/Visibility';
import StorageIcon from '@mui/icons-material/Storage';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import AppsIcon from '@mui/icons-material/Apps';
import SearchIcon from '@mui/icons-material/Search';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import HubIcon from '@mui/icons-material/Hub';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';

import { useApiContext } from '@/contexts/apiContext';
import { useUserContext } from '@/contexts/userContext';
import DataGridTable from '@/components/datagridTable';
import DrawerPanel from '@/components/drawerPanel';
import ErrorMessage from '@/components/errorMessage';
import ProgressIndicator from '@/components/progressIndicator';
import ContactUsForm from '@/components/contactUs/contactUsForm';
import { JsonViewer } from '@/components/viewers';
import { EntityType } from '@/components/entities/entityType';

interface Props {
  maxHeight?: number;
}

const PipelineBlueprintDefs = ({ maxHeight }: Props) => {
  const apiContext = useApiContext();
  const { hasRole } = useUserContext();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();

  const isAdmin = hasRole('administrator');
  const [blueprints, setBlueprints] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [apiError, setApiError] = useState<any>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');

  // Drawer details state
  const [selectedBlueprint, setSelectedBlueprint] = useState<any | null>(null);
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
  const [drawerTab, setDrawerTab] = useState<number>(0);
  const [copiedJson, setCopiedJson] = useState<boolean>(false);

  // Contact Us state for custom blueprint requests
  const [showContactUs, setShowContactUs] = useState<boolean>(false);

  const fetchBlueprints = async () => {
    try {
      setLoading(true);
      setApiError(null);
      const rawData: any = await apiContext.getPipelineBlueprints();
      const list = Array.isArray(rawData) ? rawData : rawData?.data || [];
      setBlueprints(list);
    } catch (err: any) {
      setApiError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBlueprints();
  }, []);

  const openInBuilder = (blueprintId: string) => {
    navigate(`/pipelineBuilder?blueprintId=${blueprintId}`);
  };

  const handleOpenDrawer = (blueprint: any) => {
    setSelectedBlueprint(blueprint);
    setDrawerTab(0);
    setDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
  };

  const copyBlueprintJson = () => {
    if (!selectedBlueprint) return;
    navigator.clipboard.writeText(JSON.stringify(selectedBlueprint, null, 2));
    setCopiedJson(true);
    setTimeout(() => setCopiedJson(false), 2000);
  };

  // Filtered blueprints based on search query
  const filteredBlueprints = useMemo(() => {
    if (!searchQuery.trim()) return blueprints;
    const q = searchQuery.toLowerCase();
    return blueprints.filter(
      (bp) =>
        bp.name?.toLowerCase().includes(q) ||
        bp.id?.toLowerCase().includes(q) ||
        bp.description?.toLowerCase().includes(q) ||
        bp.domainId?.toLowerCase().includes(q),
    );
  }, [blueprints, searchQuery]);

  const columns: GridColDef[] = [
    {
      field: 'id',
      headerName: t('pipelineBlueprint.pipelineBlueprintDefs.columns.id', 'Blueprint ID'),
      width: 220,
      renderCell: (params) => (
        <Typography
          variant="body2"
          sx={{
            fontFamily: 'monospace',
            fontWeight: 600,
            fontSize: '0.8rem',
            color: 'text.secondary',
          }}
        >
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'name',
      headerName: t('pipelineBlueprint.pipelineBlueprintDefs.columns.name', 'Blueprint Name'),
      flex: 1.2,
      minWidth: 200,
      renderCell: (params) => (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ height: '100%' }}>
          <AccountTreeIcon sx={{ fontSize: 18, color: 'secondary.main', flexShrink: 0 }} />
          <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary' }}>
            {params.value}
          </Typography>
        </Stack>
      ),
    },
    {
      field: 'domainId',
      headerName: t('pipelineBlueprint.pipelineBlueprintDefs.columns.domain', 'Domain'),
      width: 140,
      renderCell: (params) => {
        const domain = params.value || 'general';
        const isBioPharm = domain.toLowerCase() === 'biopharm';
        return (
          <Chip
            size="small"
            label={domain}
            sx={{
              fontWeight: 600,
              fontSize: '0.725rem',
              bgcolor: isBioPharm
                ? alpha(theme.palette.secondary.main, 0.12)
                : alpha(theme.palette.primary.main, 0.08),
              color: isBioPharm ? theme.palette.secondary.dark : theme.palette.primary.dark,
              border: '1px solid',
              borderColor: isBioPharm
                ? alpha(theme.palette.secondary.main, 0.25)
                : alpha(theme.palette.primary.main, 0.15),
            }}
          />
        );
      },
    },
    {
      field: 'components',
      headerName: t('pipelineBlueprint.pipelineBlueprintDefs.columns.components', 'DAG Components'),
      width: 220,
      valueGetter: (_value, row) => row.entities?.length || 0,
      renderCell: (params) => {
        const entities: any[] = params.row?.entities || [];
        const dsCount = entities.filter((e) => e.entityType === EntityType.dataSourceInstance).length;
        const agentCount = entities.filter((e) => e.entityType === EntityType.agentInstance).length;
        const appCount = entities.filter((e) => e.entityType === EntityType.userAppInstance).length;

        return (
          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ height: '100%' }}>
            <Tooltip title={`${entities.length} total nodes in DAG blueprint`}>
              <Chip
                size="small"
                label={`${entities.length} nodes`}
                variant="outlined"
                sx={{ fontWeight: 700, fontSize: '0.725rem' }}
              />
            </Tooltip>
            {dsCount > 0 && (
              <Tooltip title={`${dsCount} Data Sources`}>
                <Chip
                  icon={<StorageIcon sx={{ fontSize: '13px !important' }} />}
                  size="small"
                  label={dsCount}
                  sx={{
                    height: 22,
                    fontSize: '0.7rem',
                    bgcolor: alpha(theme.palette.info.main, 0.1),
                    color: theme.palette.info.dark,
                  }}
                />
              </Tooltip>
            )}
            {agentCount > 0 && (
              <Tooltip title={`${agentCount} Autonomous Agents`}>
                <Chip
                  icon={<SmartToyIcon sx={{ fontSize: '13px !important' }} />}
                  size="small"
                  label={agentCount}
                  sx={{
                    height: 22,
                    fontSize: '0.7rem',
                    bgcolor: alpha(theme.palette.secondary.main, 0.1),
                    color: theme.palette.secondary.dark,
                  }}
                />
              </Tooltip>
            )}
            {appCount > 0 && (
              <Tooltip title={`${appCount} User Apps`}>
                <Chip
                  icon={<AppsIcon sx={{ fontSize: '13px !important' }} />}
                  size="small"
                  label={appCount}
                  sx={{
                    height: 22,
                    fontSize: '0.7rem',
                    bgcolor: alpha(theme.palette.warning.main, 0.1),
                    color: theme.palette.warning.dark,
                  }}
                />
              </Tooltip>
            )}
          </Stack>
        );
      },
    },
    {
      field: 'subscriptions',
      headerName: t('pipelineBlueprint.pipelineBlueprintDefs.columns.subscriptions', 'Event Links'),
      width: 120,
      valueGetter: (_value, row) => row.subscriptions?.length || 0,
      renderCell: (params) => (
        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ height: '100%' }}>
          <HubIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
          <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
            {params.value} events
          </Typography>
        </Stack>
      ),
    },
    {
      field: 'createdBy',
      headerName: t('pipelineBlueprint.pipelineBlueprintDefs.columns.createdBy', 'Created By'),
      width: 130,
      valueFormatter: (value: string) => (value ? value.charAt(0).toUpperCase() + value.slice(1) : 'System'),
    },
    {
      field: 'createdDate',
      headerName: t('pipelineBlueprint.pipelineBlueprintDefs.columns.createdDate', 'Created Date'),
      width: 150,
      valueFormatter: (value: string) => (value ? formatRelative(new Date(value), new Date()) : '—'),
    },
    {
      field: 'actions',
      headerName: t('pipelineBlueprint.pipelineBlueprintDefs.columns.actions', 'Actions'),
      headerAlign: 'left',
      align: 'left',
      width: 100,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Stack
          direction="row"
          spacing={0.5}
          alignItems="center"
          justifyContent="flex-start"
          sx={{ width: '100%', height: '100%' }}
        >
          <Tooltip title={t('pipelineBlueprint.pipelineBlueprintDefs.actions.viewDetails', 'View Details')}>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleOpenDrawer(params.row);
              }}
              sx={{
                color: 'text.secondary',
                '&:hover': { color: 'primary.main', bgcolor: alpha(theme.palette.primary.main, 0.08) },
              }}
            >
              <VisibilityIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('pipelineBlueprint.pipelineBlueprintDefs.actions.openInBuilder', 'Open in Builder')}>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                openInBuilder(params.row.id);
              }}
              sx={{
                color: 'secondary.main',
                '&:hover': { bgcolor: alpha(theme.palette.secondary.main, 0.08) },
              }}
            >
              <LaunchIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ];

  // Entities breakdown inside Drawer
  const drawerEntities: any[] = selectedBlueprint?.entities || [];
  const drawerSubscriptions: any[] = selectedBlueprint?.subscriptions || [];
  const drawerTargetEntities: any[] = selectedBlueprint?.targetEntities || [];
  const drawerDsCount = drawerEntities.filter((e) => e.entityType === EntityType.dataSourceInstance).length;
  const drawerAgentCount = drawerEntities.filter((e) => e.entityType === EntityType.agentInstance).length;
  const drawerAppCount = drawerEntities.filter((e) => e.entityType === EntityType.userAppInstance).length;

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        border: 1,
        borderColor: 'divider',
        borderRadius: '12px',
        overflow: 'hidden',
        bgcolor: 'background.paper',
      }}
    >
      {/* Top Header Bar */}
      <Box
        sx={{
          px: 2.5,
          py: 1.5,
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'center' },
          justifyContent: 'space-between',
          gap: 1.5,
          flexShrink: 0,
        }}
      >
        <Stack spacing={0.25}>
          <Stack direction="row" spacing={1} alignItems="center">
            <AccountTreeIcon sx={{ color: 'secondary.main', fontSize: 20 }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 800, fontSize: '0.95rem' }}>
              {t('pipelineBlueprint.pipelineBlueprintDefs.header', 'Pipeline Blueprints')}
            </Typography>
            <Chip
              size="small"
              label={`${filteredBlueprints.length} available`}
              sx={{
                height: 20,
                fontSize: '0.7rem',
                fontWeight: 700,
                bgcolor: alpha(theme.palette.secondary.main, 0.1),
                color: theme.palette.secondary.dark,
              }}
            />
          </Stack>
          <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
            {t(
              'pipelineBlueprint.pipelineBlueprintDefs.subHeader',
              'Pre-engineered DAG pipeline templates connecting data sources, autonomous ReAct agents, and user applications.',
            )}
          </Typography>
          {apiError && <ErrorMessage error={apiError} />}
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center">
          <TextField
            size="small"
            placeholder="Search blueprints..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                </InputAdornment>
              ),
              sx: { height: 34, fontSize: '0.8rem', borderRadius: '8px', minWidth: 180 },
            }}
          />

          <Button
            size="small"
            variant="outlined"
            onClick={() => setShowContactUs(true)}
            startIcon={<SupportAgentIcon sx={{ fontSize: 16 }} />}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: '8px',
              fontSize: '0.775rem',
              height: 34,
              whiteSpace: 'nowrap',
            }}
          >
            {t('pipelineBlueprint.pipelineBlueprintDefs.actions.request', 'Request Custom')}
          </Button>

          <Button
            size="small"
            color="secondary"
            variant="contained"
            onClick={() => navigate('/pipelineBuilder')}
            startIcon={<AddIcon sx={{ fontSize: 16 }} />}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: '8px',
              boxShadow: 'none',
              '&:hover': { boxShadow: 'none' },
              color: 'text.primary',
              fontSize: '0.775rem',
              height: 34,
              whiteSpace: 'nowrap',
            }}
          >
            {t('pipelineBlueprint.pipelineBlueprintDefs.actions.create', 'Open in Builder')}
          </Button>
        </Stack>
      </Box>

      {/* Main Table Content */}
      <Box sx={{ flex: 1, overflow: 'hidden', p: 1, display: 'flex', flexDirection: 'column' }}>
        {loading ? (
          <ProgressIndicator />
        ) : (
          <>
            {filteredBlueprints.length === 0 ? (
              <Stack
                alignItems="center"
                justifyContent="center"
                sx={{ height: '100%', minHeight: 250, textAlign: 'center', p: 3 }}
                spacing={1.5}
              >
                <AccountTreeIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
                <Typography variant="body1" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                  {t('pipelineBlueprint.pipelineBlueprintDefs.noRecord', 'No pipeline blueprints found')}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', maxWidth: 360 }}>
                  {searchQuery
                    ? 'No blueprints match your filter criteria. Try clearing the search query.'
                    : 'Get started by creating a new pipeline configuration in the visual builder.'}
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => navigate('/pipelineBuilder')}
                  startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                  sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px', mt: 1 }}
                >
                  Create Pipeline in Visual Builder
                </Button>
              </Stack>
            ) : (
              <Box sx={{ flex: 1, minHeight: 0 }}>
                <DataGridTable
                  rows={filteredBlueprints}
                  columns={columns}
                  loading={loading}
                  pageSize={10}
                  height={maxHeight ? `${maxHeight - 75}px` : '100%'}
                />
              </Box>
            )}
          </>
        )}
      </Box>

      {/* Blueprint Details Slide-Out Drawer */}
      <DrawerPanel
        open={drawerOpen}
        onClose={handleCloseDrawer}
        minWidth="48vw"
        title={selectedBlueprint?.name || 'Blueprint Details'}
        subTitle={selectedBlueprint?.id}
      >
        {selectedBlueprint && (
          <Box sx={{ p: 2.5, height: '100%', display: 'flex', flexDirection: 'column', gap: 2, overflowY: 'auto' }}>
            {/* Quick Action & Description Banner */}
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: '12px',
                border: 1,
                borderColor: 'divider',
                bgcolor: alpha(theme.palette.secondary.main, 0.04),
              }}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
                <Stack spacing={0.75} sx={{ flex: 1 }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <AccountTreeIcon sx={{ color: 'secondary.main', fontSize: 20 }} />
                    <Typography variant="h6" sx={{ fontWeight: 800, fontSize: '1.05rem' }}>
                      {selectedBlueprint.name}
                    </Typography>
                    <Chip
                      size="small"
                      label={selectedBlueprint.domainId || 'general'}
                      sx={{
                        fontWeight: 700,
                        fontSize: '0.7rem',
                        bgcolor: alpha(theme.palette.secondary.main, 0.15),
                        color: theme.palette.secondary.dark,
                      }}
                    />
                  </Stack>
                  <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.5 }}>
                    {selectedBlueprint.description || 'No blueprint description provided.'}
                  </Typography>
                </Stack>

                <Button
                  variant="contained"
                  color="secondary"
                  size="small"
                  startIcon={<LaunchIcon sx={{ fontSize: 16 }} />}
                  onClick={() => openInBuilder(selectedBlueprint.id)}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 700,
                    borderRadius: '8px',
                    boxShadow: 'none',
                    color: 'text.primary',
                    whiteSpace: 'nowrap',
                    px: 2,
                  }}
                >
                  Open in Builder
                </Button>
              </Stack>
            </Paper>

            {/* Quick Stats Grid */}
            <Grid container spacing={1.5}>
              <Grid size={{ xs: 6, sm: 3 }}>
                <Card variant="outlined" sx={{ p: 1.5, borderRadius: '10px' }}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                    <StorageIcon sx={{ fontSize: 16, color: 'info.main' }} />
                    <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                      Data Sources
                    </Typography>
                  </Stack>
                  <Typography variant="h6" sx={{ fontWeight: 800 }}>
                    {drawerDsCount}
                  </Typography>
                </Card>
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}>
                <Card variant="outlined" sx={{ p: 1.5, borderRadius: '10px' }}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                    <SmartToyIcon sx={{ fontSize: 16, color: 'secondary.main' }} />
                    <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                      ReAct Agents
                    </Typography>
                  </Stack>
                  <Typography variant="h6" sx={{ fontWeight: 800 }}>
                    {drawerAgentCount}
                  </Typography>
                </Card>
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}>
                <Card variant="outlined" sx={{ p: 1.5, borderRadius: '10px' }}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                    <AppsIcon sx={{ fontSize: 16, color: 'warning.main' }} />
                    <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                      User Apps
                    </Typography>
                  </Stack>
                  <Typography variant="h6" sx={{ fontWeight: 800 }}>
                    {drawerAppCount}
                  </Typography>
                </Card>
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}>
                <Card variant="outlined" sx={{ p: 1.5, borderRadius: '10px' }}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                    <HubIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                    <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                      Event Links
                    </Typography>
                  </Stack>
                  <Typography variant="h6" sx={{ fontWeight: 800 }}>
                    {drawerSubscriptions.length}
                  </Typography>
                </Card>
              </Grid>
            </Grid>

            {/* Sub-tabs inside Drawer */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs
                value={drawerTab}
                onChange={(_e, v) => setDrawerTab(v)}
                indicatorColor="secondary"
                textColor="secondary"
                sx={{ minHeight: 38 }}
              >
                <Tab
                  label="DAG Nodes & Wirings"
                  sx={{ minHeight: 38, textTransform: 'none', fontWeight: 700, fontSize: '0.8rem' }}
                />
                <Tab
                  label="Raw Blueprint JSON"
                  sx={{ minHeight: 38, textTransform: 'none', fontWeight: 700, fontSize: '0.8rem' }}
                />
              </Tabs>
            </Box>

            {/* Tab 0: Architecture components */}
            {drawerTab === 0 && (
              <Stack spacing={2.5}>
                {/* Entities List */}
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 750, mb: 1 }}>
                    Pipeline Components ({drawerEntities.length})
                  </Typography>
                  <Stack spacing={1}>
                    {drawerEntities.map((ent: any, idx: number) => {
                      const isDs = ent.entityType === EntityType.dataSourceInstance;
                      const isAgent = ent.entityType === EntityType.agentInstance;
                      const isApp = ent.entityType === EntityType.userAppInstance;

                      return (
                        <Paper
                          key={ent.id || idx}
                          variant="outlined"
                          sx={{
                            p: 1.25,
                            borderRadius: '8px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            bgcolor: 'background.paper',
                          }}
                        >
                          <Stack direction="row" spacing={1.5} alignItems="center">
                            <Box
                              sx={{
                                width: 24,
                                height: 24,
                                borderRadius: '6px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                bgcolor: alpha(theme.palette.text.primary, 0.05),
                                fontSize: '0.75rem',
                                fontWeight: 800,
                              }}
                            >
                              {ent.id || idx + 1}
                            </Box>
                            <Box
                              sx={{
                                p: 0.5,
                                borderRadius: '6px',
                                bgcolor: isDs
                                  ? alpha(theme.palette.info.main, 0.1)
                                  : isAgent
                                    ? alpha(theme.palette.secondary.main, 0.1)
                                    : alpha(theme.palette.warning.main, 0.1),
                                color: isDs
                                  ? theme.palette.info.main
                                  : isAgent
                                    ? theme.palette.secondary.main
                                    : theme.palette.warning.main,
                                display: 'flex',
                              }}
                            >
                              {isDs && <StorageIcon sx={{ fontSize: 16 }} />}
                              {isAgent && <SmartToyIcon sx={{ fontSize: 16 }} />}
                              {isApp && <AppsIcon sx={{ fontSize: 16 }} />}
                            </Box>
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                {ent.entityDefId}
                              </Typography>
                              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                {isDs ? 'Data Source' : isAgent ? 'Autonomous Agent' : 'User Application'}
                              </Typography>
                            </Box>
                          </Stack>

                          {ent.settings && Object.keys(ent.settings).length > 0 && (
                            <Chip
                              size="small"
                              variant="outlined"
                              label={`${Object.keys(ent.settings).length} settings configured`}
                              sx={{ fontSize: '0.675rem', height: 20 }}
                            />
                          )}
                        </Paper>
                      );
                    })}
                  </Stack>
                </Box>

                {/* Subscriptions / Event Links */}
                {drawerSubscriptions.length > 0 && (
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 750, mb: 1 }}>
                      Event Subscriptions ({drawerSubscriptions.length})
                    </Typography>
                    <Stack spacing={0.75}>
                      {drawerSubscriptions.map((sub: any, idx: number) => {
                        const srcEnt = drawerEntities.find((e) => String(e.id) === String(sub.source));
                        const tgtEnt = drawerEntities.find((e) => String(e.id) === String(sub.target));

                        return (
                          <Paper
                            key={idx}
                            variant="outlined"
                            sx={{
                              p: 1,
                              borderRadius: '8px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              fontSize: '0.8rem',
                            }}
                          >
                            <Typography variant="caption" sx={{ fontWeight: 700, fontFamily: 'monospace' }}>
                              #{sub.source} {srcEnt?.entityDefId || ''}
                            </Typography>
                            <Chip
                              size="small"
                              label={sub.eventName}
                              sx={{
                                height: 20,
                                fontSize: '0.675rem',
                                fontWeight: 700,
                                bgcolor: alpha(theme.palette.secondary.main, 0.1),
                                color: theme.palette.secondary.dark,
                              }}
                            />
                            <Typography variant="caption" sx={{ fontWeight: 700, fontFamily: 'monospace' }}>
                              → #{sub.target} {tgtEnt?.entityDefId || ''}
                            </Typography>
                          </Paper>
                        );
                      })}
                    </Stack>
                  </Box>
                )}

                {/* Target Entities / Sinks */}
                {drawerTargetEntities.length > 0 && (
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 750, mb: 1 }}>
                      Target App Connections ({drawerTargetEntities.length})
                    </Typography>
                    <Stack spacing={0.75}>
                      {drawerTargetEntities.map((tgt: any, idx: number) => {
                        const srcEnt = drawerEntities.find((e) => String(e.id) === String(tgt.source));
                        const tgtEnt = drawerEntities.find((e) => String(e.id) === String(tgt.target));

                        return (
                          <Paper
                            key={idx}
                            variant="outlined"
                            sx={{
                              p: 1,
                              borderRadius: '8px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              fontSize: '0.8rem',
                            }}
                          >
                            <Typography variant="caption" sx={{ fontWeight: 700, fontFamily: 'monospace' }}>
                              #{tgt.source} {srcEnt?.entityDefId || ''}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                              connected to UI app
                            </Typography>
                            <Typography variant="caption" sx={{ fontWeight: 700, fontFamily: 'monospace' }}>
                              #{tgt.target} {tgtEnt?.entityDefId || ''}
                            </Typography>
                          </Paper>
                        );
                      })}
                    </Stack>
                  </Box>
                )}
              </Stack>
            )}

            {/* Tab 1: Raw JSON view */}
            {drawerTab === 1 && (
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Stack direction="row" justifyContent="flex-end">
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={copiedJson ? <CheckIcon color="success" /> : <ContentCopyIcon sx={{ fontSize: 16 }} />}
                    onClick={copyBlueprintJson}
                    sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px' }}
                  >
                    {copiedJson ? 'Copied' : 'Copy Blueprint JSON'}
                  </Button>
                </Stack>
                <Box
                  sx={{
                    flex: 1,
                    maxHeight: 450,
                    overflowY: 'auto',
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: '8px',
                    p: 1,
                  }}
                >
                  <JsonViewer json={selectedBlueprint} showLabel={false} maxHeight={450} />
                </Box>
              </Box>
            )}
          </Box>
        )}
      </DrawerPanel>

      {/* Contact Us drawer for requesting custom blueprints */}
      <DrawerPanel open={showContactUs} onClose={() => setShowContactUs(false)} minWidth={'40vw'}>
        <ContactUsForm onClose={() => setShowContactUs(false)} />
      </DrawerPanel>
    </Box>
  );
};

export default PipelineBlueprintDefs;
